import {
	useCallback, useEffect, useRef, useState,
} from 'react';
import {
	AuiIf,
	ThreadPrimitive,
	useAuiState,
} from '@assistant-ui/react';
import { cn } from '@uconnect/assets/lib/utils';
import {
	Tabs, TabsList, TabsTrigger, TabsContent,
} from '@uconnect/assets/components/ui/tabs';
import { Button } from '@uconnect/assets/components/ui/button';
import Icon from '@uconnect/assets/components/ui/icon';
import { toast } from '@uconnect/assets/components/ui/sonner';
import { Toaster } from '@uconnect/assets/components/ui/sonner';
import { useNewChat } from './RuntimeProvider';
import EmptyThread from './EmptyThread';
import SearchQuota from './SearchQuota';
import useSearchQuota from './useSearchQuota';
import type { AgentUIConfig } from './types';
import {
	clearThreads,
	deleteThread,
	fetchThreads,
	fetchThreadState,
	mapThreadStateMessages,
	type ThreadListItem,
} from './chatApi';
import { AgentApiError, getAgentErrorMessage } from '../../common/js/errors';
import {
	UserMessage,
	AssistantMessage,
	Composer,
	ThreadHistoryPanel,
} from '../../common/js/components';
import type { FeedbackConfig } from '../../common/js/components/AssistantMessage';
import { useAgentAnalytics } from './hooks/useAgentAnalytics';
import { RestoringSearchSkeletonLoader, SavedSearchesSkeletonLoader } from './components/skeletons';
import ErrorMessage from './components/ErrorMessage';

interface AgentUIProps {
	config?: AgentUIConfig;
}

type AgentTab = 'this-search' | 'search-history';

export default function AgentUI({ config = {} }: AgentUIProps) {
	const {
		context = 'default',
		className = '',
		isExpanded = false,
		feedback = {
			enabled: true,
			positiveMessage: 'Thanks for the feedback',
			negativeMessage: 'Thank you; your feedback helps us improve AI Search',
		},
		containerMaxWidthClasses = 'w-full mx-auto! transition-all'
	} = config;
	const { isLoggedIn = false, userId } = config;
	const showSearchHistory = Boolean(isLoggedIn && userId);

	return (
		<>
			<AgentUIContent
				config={config}
				className={className}
				context={context}
				isExpanded={isExpanded}
				showSearchHistory={showSearchHistory}
				feedback={feedback}
				containerMaxWidthClasses={containerMaxWidthClasses}
			/>
			<Toaster
				{...config.toasterProps}
			/>
		</>
	);
}

function getApiErrorMessage(error: unknown, fallback: string): string {
	return error instanceof AgentApiError ? getAgentErrorMessage(error) : fallback;
}

function AgentUIContent({
	config,
	className,
	containerMaxWidthClasses,
	context,
	isExpanded,
	showSearchHistory,
	feedback,
}: {
	config: AgentUIConfig;
	className: string;
	containerMaxWidthClasses: string;
	context: NonNullable<AgentUIConfig['context']>;
	isExpanded: boolean;
	showSearchHistory: boolean;
	feedback: FeedbackConfig;
}) {
	const [activeTab, setActiveTab] = useState<AgentTab>('this-search');
	const [tabSwitchAnnouncement, setTabSwitchAnnouncement] = useState('');
	const {
		clearHistory,
		resumeThread,
		messagesSentCount,
		threadId,
		activeThreadId,
		startNewChat,
		stopResponse,
		isResponding,
		isResumedThread,
		isRestoringThread,
		lastUserPrompt,
		wasStopped,
		lastError,
		retryLastMessage,
	} = useNewChat();
	const messages = useAuiState((s) => s.thread.messages);

	useAgentAnalytics({
		threadId,
		messagesSentCount,
		isLoggedIn: Boolean(config.isLoggedIn),
		isResumedThread,
	});

	const BoundAssistantMessage = useCallback(
		() => <AssistantMessage feedback={feedback} messageClassNames="p-0!" ucRef="ai_search" threadId={threadId} />,
		[ feedback, threadId ]
	);

	const maxGuestSearches = Number( config.maxGuestSearches ?? 1 );
	const { remainingSearches, decrementQuota } = useSearchQuota(Boolean(config.isLoggedIn), maxGuestSearches);
	const prevMessagesSentCount = useRef(messagesSentCount);
	const prevActiveThreadId = useRef<string | null>(null);
	useEffect(() => {
		if (messagesSentCount > prevMessagesSentCount.current) {
			decrementQuota();
			if (activeTab === 'search-history') {
				setActiveTab('this-search');
				setTabSwitchAnnouncement('Navigated to the search results tab.');
			}
		}
		prevMessagesSentCount.current = messagesSentCount;
	}, [messagesSentCount, activeTab, decrementQuota]);

	const isUnauthenticatedSearchRestricted = !config.isLoggedIn && maxGuestSearches === 0;
	const chatDisabled = isUnauthenticatedSearchRestricted || (!config.isLoggedIn && remainingSearches <= 0);
	const newSearchDisabled = chatDisabled;
	const [threadSummaries, setThreadSummaries] = useState<ThreadListItem[]>([]);
	const [isLoadingThreads, setIsLoadingThreads] = useState(false);
	const [isLoadingSelectedThread, setIsLoadingSelectedThread] = useState(false);
	const [isClearingHistory, setIsClearingHistory] = useState(false);
	const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
	const [threadError, setThreadError] = useState<string | null>(null);
	const [searchHistoryStatusMessage, setSearchHistoryStatusMessage] = useState('');
	const [composerPrefill, setComposerPrefill] = useState('');
	const [composerPrefillRequestId, setComposerPrefillRequestId] = useState(0);

	// Track when a thread successfully loads
	useEffect(() => {
		if (activeThreadId && !isLoadingSelectedThread && messages.length > 0) {
			prevActiveThreadId.current = activeThreadId;
		}
	}, [activeThreadId, isLoadingSelectedThread, messages.length]);

	const triggerComposerPrefill = useCallback((prompt: string) => {
		if (!prompt) {
			return;
		}

		setComposerPrefill(prompt);
		setComposerPrefillRequestId((value) => value + 1);
	}, []);

	useEffect(() => {
		if (wasStopped) {
			triggerComposerPrefill(lastUserPrompt);
		}
	}, [wasStopped, lastUserPrompt, triggerComposerPrefill]);

	const loadThreadSummaries = useCallback(async () => {
		if (!showSearchHistory || !config.agentHost || !config.siteId) {
			setThreadSummaries([]);
			return;
		}
		setThreadError(null);
		setSearchHistoryStatusMessage('Loading saved searches.');

		// Prevent a flash of the skeleton loaders if the API responds quickly by only showing them after a short delay
		const loadingTimeoutId = setTimeout(() => {
			setIsLoadingThreads(true);
		}, 200);

		try {
			const summaries = await fetchThreads({
				agentHost: config.agentHost,
				siteId: config.siteId,
			});
			clearTimeout(loadingTimeoutId);
			setIsLoadingThreads(false);
			setThreadSummaries(summaries);
			setSearchHistoryStatusMessage(`Loaded ${summaries.length} saved ${summaries.length === 1 ? 'search' : 'searches'}.`);
		} catch (error) {
			clearTimeout(loadingTimeoutId);
			setIsLoadingThreads(false);
			setThreadSummaries([]);
			setThreadError(getApiErrorMessage(error, 'Failed to load search history.'));
			setSearchHistoryStatusMessage('Failed to load saved searches.');
		}
	}, [showSearchHistory, config.agentHost, config.siteId]);

	useEffect(() => {
		setThreadError(null);
		void loadThreadSummaries();
	}, [loadThreadSummaries]);

	useEffect(() => {
		if (activeTab === 'search-history') {
			void loadThreadSummaries();
		}
	}, [activeTab, loadThreadSummaries]);

	const handleClearHistory = async () => {
		if (!config.agentHost || !config.siteId) {
			setThreadSummaries([]);
			clearHistory();
			return;
		}
		if (threadSummaries.length === 0) {
			clearHistory();
			return;
		}

		setIsClearingHistory(true);
		setThreadError(null);
		setSearchHistoryStatusMessage('Clearing search history.');
		try {
			await clearThreads({
				agentHost: config.agentHost,
				siteId: config.siteId,
			});
			await loadThreadSummaries();
			clearHistory();
			setSearchHistoryStatusMessage('Search history cleared.');
		} catch (error) {
			setThreadError(getApiErrorMessage(error, 'Unable to clear history. Please try again.'));
			setSearchHistoryStatusMessage('Unable to clear search history.');
		} finally {
			setIsClearingHistory(false);
		}
	};

	const handleSelectThread = async (threadId: string) => {
		if (!config.agentHost || !config.siteId) {
			return;
		}

		const selectedSummary = threadSummaries.find(
			(thread) => thread.thread_id === threadId
		);
		if (!selectedSummary) {
			return;
		}

		try {
			setActiveTab('this-search');
			setThreadError(null);

			// Delay showing loading skeleton by 200ms; if fetch completes quickly, skeleton never appears
			const loadingTimeoutId = setTimeout(() => {
				setIsLoadingSelectedThread(true);
			}, 200);

			const threadState = await fetchThreadState({
				agentHost: config.agentHost,
				siteId: config.siteId,
				threadId,
			});
			clearTimeout(loadingTimeoutId);
			setIsLoadingSelectedThread(false);
			await resumeThread(
				threadId,
				mapThreadStateMessages(threadState.messages ?? [])
			);
		} catch (error) {
			setIsLoadingSelectedThread(false);
			setThreadError(getApiErrorMessage(error, 'Failed to load that conversation.'));
		}
	};

	const handleDeleteThread = async (threadId: string) => {
		if (!config.agentHost || !config.siteId || deletingThreadId) {
			return;
		}

		setDeletingThreadId(threadId);
		setThreadError(null);

		try {
			const deletedThreadTitle = threadSummaries.find((thread) => thread.thread_id === threadId)?.title;
			setSearchHistoryStatusMessage('Deleting conversation.');
			await deleteThread({
				agentHost: config.agentHost,
				siteId: config.siteId,
				threadId,
			});
			setThreadSummaries((summaries) => {
				const updatedSummaries = summaries.filter((thread) => thread.thread_id !== threadId);
				const deletedLabel = deletedThreadTitle ? `Conversation \"${deletedThreadTitle}\" deleted.` : 'Conversation deleted.';
				setSearchHistoryStatusMessage(`${deletedLabel} ${updatedSummaries.length} saved ${updatedSummaries.length === 1 ? 'search remains' : 'searches remain'}.`);
				return updatedSummaries;
			});

			if (activeThreadId === threadId) {
				clearHistory();
			}
		} catch (error) {
			setSearchHistoryStatusMessage('Unable to delete conversation.');
			toast.error({
				title: 'Unable to delete conversation',
				description: getApiErrorMessage(error, 'Unable to delete conversation.'),
			});
		} finally {
			setDeletingThreadId(null);
		}
	};

	return (
		<div
			className={cn(
				`agent-ui-container agent-ui--${context}`,
				'mx-auto! h-full w-full min-h-96 max-h-screen min-w-0 flex! flex-col bg-white',
				containerMaxWidthClasses,
				className
			)}
		>
			<div className="flex-1 overflow-hidden flex flex-col">
				<ThreadPrimitive.Root className="flex gap-4 flex-col h-full bg-white overflow-hidden grow">
					{showSearchHistory ? (
						<Tabs
							variant="underline"
							value={activeTab}
							onValueChange={(v) => setActiveTab(v as AgentTab)}
							className="flex-1 flex flex-col min-h-0 gap-0"
						>
							<div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
								{tabSwitchAnnouncement}
							</div>
							<TabsList
								className={cn(
									'border-[#d1d1d1] text-sm',
									isExpanded && containerMaxWidthClasses
								)}
							>
								<TabsTrigger value="this-search" className="font-default!">
									This Search
								</TabsTrigger>
								<TabsTrigger value="search-history" className="font-default!">
									Search History
								</TabsTrigger>
							</TabsList>

							<TabsContent
								value="this-search"
								className="flex-1 flex flex-col min-h-0 mt-0"
								tabIndex={-1}
							>
								<ThreadPrimitive.Viewport
									role="log"
									aria-live="polite"
									aria-relevant="additions text"
									aria-label="Conversation transcript"
									aria-busy={isLoadingSelectedThread}
									className={cn(
										'flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 p-5! max-w-full',
										isExpanded && containerMaxWidthClasses
									)}
								>
									<AuiIf condition={() => isLoadingSelectedThread || isRestoringThread}>
										<RestoringSearchSkeletonLoader />
									</AuiIf>
									<AuiIf condition={() => !isLoadingSelectedThread && !isRestoringThread}>
										<>
											<AuiIf condition={(s) => s.thread.isEmpty}>
												<EmptyThread
													chatDisabled={chatDisabled}
													isUnauthenticatedSearchRestricted={isUnauthenticatedSearchRestricted}
													signInUrl={config.signInUrl}
													onSignIn={config.onSignIn}
													initialPrompts={config.initialPrompts}
													isLoggedIn={ config.isLoggedIn }
													firstName={ config.firstName }
													personalization={ config.personalization }
													contentPreferencesUrl={ config.contentPreferencesUrl }
												/>
											</AuiIf>
											<ThreadPrimitive.Messages
												components={{ UserMessage, AssistantMessage: BoundAssistantMessage }}
											/>
											{ lastError && (
												<ErrorMessage
													error={ lastError }
													onRetry={ retryLastMessage }
												/>
											) }
										</>
									</AuiIf>
								</ThreadPrimitive.Viewport>
							</TabsContent>

							<TabsContent
								value="search-history"
								className="flex-1 flex flex-col min-h-0 mt-0"
								tabIndex={-1}
							>
								<ThreadHistoryPanel
									variant="search"
									isExpanded={isExpanded}
									containerMaxWidthClasses={containerMaxWidthClasses}
									savedThreads={threadSummaries}
									isLoading={isLoadingThreads}
									isClearing={isClearingHistory}
									deletingThreadId={deletingThreadId}
									errorMessage={threadError}
									statusMessage={searchHistoryStatusMessage}
									onClearHistory={handleClearHistory}
									onSelectThread={handleSelectThread}
									onDeleteThread={handleDeleteThread}
								/>
							</TabsContent>
						</Tabs>
					) : (
						<ThreadPrimitive.Viewport
							role="log"
							aria-live="polite"
							aria-relevant="additions text"
							aria-label="Conversation transcript"
							aria-busy={isRestoringThread}
							className={cn(
								'flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 p-5! max-w-full',
								isExpanded && containerMaxWidthClasses
							)}
						>
							<AuiIf condition={(s) => s.thread.isEmpty}>
								<EmptyThread
									chatDisabled={chatDisabled}
									isUnauthenticatedSearchRestricted={isUnauthenticatedSearchRestricted}
									signInUrl={config.signInUrl}
									onSignIn={config.onSignIn}
									initialPrompts={config.initialPrompts}
									isLoggedIn={ config.isLoggedIn }
									firstName={ config.firstName }
									personalization={ config.personalization }
									contentPreferencesUrl={ config.contentPreferencesUrl }
								/>
							</AuiIf>
							<ThreadPrimitive.Messages
								components={{ UserMessage, AssistantMessage: BoundAssistantMessage }}
							/>
							{ lastError && (
								<ErrorMessage
									error={ lastError }
									onRetry={ retryLastMessage }
								/>
							) }
						</ThreadPrimitive.Viewport>
					)}

					{
						// Guest mode, and only when there are remaining searches or an active thread.
						// There is an alternate layout in EmptyThread when there are no remaining searches, so this
						// prevents the message from being doubled-up.
						!config.isLoggedIn
						&& (!showSearchHistory || activeTab === 'this-search')
						&& (
							(messages.length > 0 && (!activeThreadId || activeThreadId === prevActiveThreadId.current))
							|| !chatDisabled
						)
						&& (
							<SearchQuota
								remainingSearches={remainingSearches}
								maxGuestSearches={maxGuestSearches}
								isExpanded={isExpanded}
								signInUrl={config.signInUrl}
								onSignIn={config.onSignIn}
								containerMaxWidthClasses={containerMaxWidthClasses}
							/>
						)
					}

					<Composer
						isExpanded={isExpanded}
						context={context}
						icon={config.fabIcon || undefined}
						chatDisabled={chatDisabled}
						isResponding={isResponding}
						onStop={stopResponse}
						prefillText={composerPrefill}
						prefillRequestId={composerPrefillRequestId}
						containerMaxWidthClasses={containerMaxWidthClasses}
					/>
				</ThreadPrimitive.Root>
			</div>
		</div>
	);
}
